import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { animated, useSpring } from '@react-spring/web';
import { bs } from '../../../styles/styles';
import { StarIcon } from '../../svgs/icons/StarIcon';
import { styleSheet } from './styles';

interface IProps {
	animateRatingChanges?: boolean;
	className?: string;
	fillColor?: string;
	onValueChanged?(value: number): void;
	readonly?: boolean;
	starStyles?: StyleDeclarationValue[];
	strokeColor?: string;
	styles?: StyleDeclarationValue[];
	total: number;
	value?: number;
	showEmoji?: boolean;
}

/**
 * Static rating definitions with emojis and labels
 * Note: Index 0 is "Please Rate" (no rating), indices 1-5 correspond to star ratings
 */
const STATIC_RATINGS = [
	{
		emoji: '🤩', // Static 4-star emoji for "Please Rate" state
		name: ' ',
		value: -1, // -1 indicates no rating selected
	},
	{
		emoji: '😞',
		name: 'Very Poor',
		value: 1, // 1 star rating
	},
	{
		emoji: '🙁',
		name: 'Poor',
		value: 2, // 2 star rating
	},
	{
		emoji: '🙂',
		name: 'Good',
		value: 3, // 3 star rating
	},
	{
		emoji: '🤩',
		name: 'Very Good',
		value: 4, // 4 star rating
	},
	{
		emoji: '🥰',
		name: 'Excellent',
		value: 5, // 5 star rating
	},
];

export const StarRating: React.FC<IProps> = ({
	animateRatingChanges,
	className,
	fillColor,
	onValueChanged,
	readonly,
	starStyles = [],
	strokeColor,
	styles = [],
	total,
	value,
	showEmoji = false,
}) => {
	const canAnimateRatingChanges = animateRatingChanges && !readonly;

	// Rating state: -1 = no rating, 1-5 = star ratings
	// Note: value=0 is treated as "no rating" to distinguish from actual star selections
	const [rating, setRating] = React.useState<number>(value !== undefined && value > 0 ? value : -1);

	// Hover state management
	const [, setHovering] = React.useState<boolean>(true);
	const [hoverIndex, setHoverIndex] = React.useState<number>(-1);

	/**
	 * Syncs internal rating state with external value prop
	 * Treats value=0 as "no rating"
	 */
	React.useEffect(() => {
		if (value !== undefined && value > 0) {
			setRating(value);
		} else {
			setRating(-1); // Reset to no rating when value is undefined OR 0
		}
	}, [value]);

	/**
	 * Handles star click events and updates rating state
	 * Rating values: 1 = 1 star, 2 = 2 stars, etc.
	 */
	const onStarClicked = React.useCallback(
		(index: number) => (e: React.MouseEvent<HTMLElement>) => {
			const nextValue = index + 1;
			if (onValueChanged) {
				onValueChanged(nextValue);
			} else {
				setRating(nextValue);
			}
			e.preventDefault();
			e.stopPropagation();
		},
		[onValueChanged]
	);

	/**
	 * Handles container-level hover events for star animations
	 */
	const onToggleHover = React.useCallback(
		(on: boolean) => () => {
			setHovering(on);
			if (!on) {
				setHoverIndex(-1);
			}
		},
		[]
	);

	/**
	 * Handles individual star hover events for preview ratings
	 */
	const onToggleStarHover = React.useCallback(
		(on: boolean, index: number) => () => {
			if (on) {
				setHoverIndex(index);
			}
		},
		[]
	);

	/**
	 * Determines which emoji and label to display based on current state
	 * Priority: hover preview > selected rating > static "Please Rate" emoji
	 */
	const getEmojiAndName = () => {
		if (hoverIndex >= 0) {
			// When hovering, show the emoji for the hovered star (index + 1)
			return STATIC_RATINGS[hoverIndex + 1];
		}
		if (rating >= 0) {
			// When rated, show the emoji for the selected rating
			return STATIC_RATINGS[rating];
		}
		// When no rating (rating === -1), show the "Please Rate" emoji
		return STATIC_RATINGS[0];
	};

	const animationStarStyles = canAnimateRatingChanges ? [styleSheet.starAnimatable, styleSheet.starHover] : [];

	/**
	 * Animation configuration for emoji display
	 */
	const emojiAnimation = useSpring({
		opacity: 1,
		transform: `translateY(0px)`,
		config: { tension: 200, friction: 15, clamp: true },
	});

	return (
		<>
			{showEmoji ? (
				<div className={css(bs.fullWidth)}>
					<animated.figure
						style={emojiAnimation}
						className={css(bs.flex, bs.flexCenter, bs.flexCol, bs.gap2, styleSheet.emojiRating)}
					>
						<picture>{getEmojiAndName()?.emoji}</picture>
						<figcaption className={css(bs.flex, bs.flexCenter)}>{getEmojiAndName()?.name}</figcaption>
					</animated.figure>
				</div>
			) : null}
			<div
				role='button'
				className={`${css(styleSheet.container, ...styles)} star-rating ${className || ''}`}
				onMouseEnter={canAnimateRatingChanges ? onToggleHover(true) : undefined}
				onMouseLeave={canAnimateRatingChanges ? onToggleHover(false) : undefined}
			>
				{Array(total)
					.fill(null)
					.map((__, i) => {
						const filled = hoverIndex >= 0 ? i <= hoverIndex : i < rating;
						const icon = (
							<StarIcon
								fillColor={fillColor}
								filled={filled}
								onMouseEnter={canAnimateRatingChanges ? onToggleStarHover(true, i) : undefined}
								onMouseLeave={canAnimateRatingChanges ? onToggleStarHover(false, i) : undefined}
								strokeColor={strokeColor}
								styles={[styleSheet.star, ...animationStarStyles, ...starStyles]}
							/>
						);
						if (readonly) {
							return <span key={`${i}`}>{icon}</span>;
						}
						return (
							<button
								key={`${i}`}
								onClick={onStarClicked(i)}
								onFocus={canAnimateRatingChanges ? onToggleStarHover(true, i) : undefined}
								onBlur={canAnimateRatingChanges ? onToggleStarHover(false, i) : undefined}
							>
								{icon}
							</button>
						);
					})}
			</div>
		</>
	);
};
